import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SignOut, Plus, Gavel, Users, Key, ShieldCheck, Package, Boat } from "@phosphor-icons/react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

// Radish brand palette on a deep-navy TopBar. Text is white, secondary text
// is a low-opacity white for hierarchy, and primary CTA / cyan-accent lives
// on the brand colour so it pops against the dark surface.
const NAV_BTN = "rounded-none h-9 px-3 bg-transparent border border-white/15 text-white hover:bg-white/10 hover:text-white hover:border-white/25";

export const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = !!user?.is_super_admin;
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-40 bg-[#0B1929] border-b border-white/10 text-white"
      data-testid="top-bar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="w-7 h-7 bg-[#22D3EE] flex items-center justify-center">
            <Gavel size={16} weight="bold" color="#0B1929" />
          </div>
          <div className="font-display text-base leading-none tracking-tight">
            INVERSE<span className="text-[#22D3EE]">.</span>
          </div>
          <div className="hidden md:block text-[10px] tracking-[0.25em] uppercase text-white/50 ml-3 border-l border-white/15 pl-3">
            Reverse Auction Platform
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {user && (
            <>
              <div className="hidden sm:flex flex-col items-end leading-tight mr-2">
                <span className="text-xs font-medium text-white" data-testid="user-name">
                  {user.name}
                  {isAdmin && (
                    <span className="ml-2 inline-block bg-[#22D3EE] text-[#0B1929] px-1.5 py-0.5 text-[9px] tracking-[0.2em] uppercase font-semibold" data-testid="admin-badge">
                      Admin
                    </span>
                  )}
                </span>
                <span className="text-[10px] tracking-[0.18em] uppercase text-white/50">{user.email}</span>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/bop-auctions")}
                className={NAV_BTN}
                data-testid="bop-nav"
              >
                <Package size={14} weight="bold" className="mr-1.5" />
                BOP RFQs
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/freight")}
                className={NAV_BTN}
                data-testid="freight-nav"
              >
                <Boat size={14} weight="bold" className="mr-1.5" />
                Freight
              </Button>
              {isAdmin && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/admin/users")}
                    className={NAV_BTN}
                    data-testid="users-nav"
                  >
                    <Users size={14} weight="bold" className="mr-1.5" />
                    Users
                  </Button>
                  <Button
                    onClick={() => navigate("/auctions/new")}
                    className="rounded-none bg-[#22D3EE] hover:bg-[#67E8F9] text-[#0B1929] btn-sharp h-9 px-4 font-semibold"
                    data-testid="create-auction-nav"
                  >
                    <Plus size={14} weight="bold" className="mr-1.5" />
                    New Auction
                  </Button>
                </>
              )}
              {isSuperAdmin && (
                <Button
                  variant="outline"
                  onClick={() => navigate("/super-admin/peek-log")}
                  className="rounded-none h-9 px-3 bg-transparent border border-[#22D3EE] text-[#22D3EE] hover:bg-[#22D3EE]/10 hover:text-[#22D3EE]"
                  title="Peek audit log"
                  data-testid="peek-log-nav"
                >
                  <ShieldCheck size={14} weight="bold" className="mr-1.5" />
                  Peek Log
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setPwOpen(true)}
                className={`${NAV_BTN} w-9 p-0`}
                title="Change password"
                data-testid="change-password-btn"
              >
                <Key size={14} weight="bold" />
              </Button>
              <Button
                variant="outline"
                onClick={async () => { await logout(); navigate("/login"); }}
                className={NAV_BTN}
                data-testid="logout-btn"
              >
                <SignOut size={14} weight="bold" />
              </Button>
            </>
          )}
        </div>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
};
